// frontend/app/components/lessonPlans/lessonBrowseGroups.js
//
// Turns a flat lesson list into the two-level tree the browse control walks:
// subject, then year, then the lessons.
//
// WHY `subject_name` AND NOT `curriculum_subject`. Two subject fields reach the
// client. `subject_name` is the lesson's own, and it is what a teacher thinks
// in — "Biology", "Chemistry", "Physics". `curriculum_subject` is how the
// curriculum records it, which is the right field for matching a lesson to a
// syllabus and the wrong one for a heading somebody reads.
//
// WHY NULLS GET THEIR OWN GROUP. `grade_level` is derived from the curriculum,
// not authored, so a lesson whose chapter the derivation cannot place arrives
// with null — and so does every lesson served by a build older than the field.
// Dropping those would mean a browse tree that silently holds fewer lessons
// than the library does, which is the one failure a browse tree cannot have:
// its whole promise is that it shows what exists. An honest ugly group is the
// cheaper mistake, so nulls are collected rather than discarded.

/** Group keys for lessons the tree cannot place. Never a real subject or year. */
export const UNPLACED_SUBJECT = "__no_subject__";
export const UNPLACED_GRADE = "__no_grade__";

/**
 * `grade_level` as a year number, or null.
 *
 * Guarded against `Number(null) === 0`: an unplaced lesson coerced that way
 * would land in a "Year 0" group, which is worse than the null group because it
 * looks like a real answer.
 */
export const toGradeYear = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const year = Number(value);
  return Number.isFinite(year) ? year : null;
};

const subjectKeyOf = (lesson) => {
  const name = typeof lesson?.subject_name === "string" ? lesson.subject_name.trim() : "";
  return name || UNPLACED_SUBJECT;
};

/** Lesson number first, because that is the order a chapter is taught in. */
const byLesson = (a, b) => {
  const left = Number(a?.lesson_no);
  const right = Number(b?.lesson_no);
  if (Number.isFinite(left) && Number.isFinite(right) && left !== right) {
    return left - right;
  }
  return String(a?.title || "").localeCompare(String(b?.title || ""));
};

/**
 * `[{ key, name, count, grades: [{ key, grade, count, lessons }] }]`
 *
 * Subjects alphabetical, years ascending, the unplaced group last at both
 * levels — a teacher scanning for Biology should not have to read past
 * "Year not set" to find it, but must still be able to reach it.
 *
 * `count` is on every node because it is what a teacher decides the next tap
 * with: a year holding two lessons and a year holding twenty-one are different
 * propositions, and the count is the only thing that says so before the tap.
 *
 * `board` rides on the year node, taken from the lessons in it, because what a
 * school calls Year 10 depends on the syllabus it teaches — Year, Class or
 * Grade. Carried per group rather than once for the tree so a list that spans
 * two boards labels each year in its own board's word instead of in whichever
 * board happened to be first.
 */
export const groupLessonsForBrowse = (lessons = []) => {
  const subjects = new Map();

  for (const lesson of Array.isArray(lessons) ? lessons : []) {
    if (!lesson) continue;

    const key = subjectKeyOf(lesson);
    if (!subjects.has(key)) {
      subjects.set(key, {
        key,
        name: key === UNPLACED_SUBJECT ? null : key,
        count: 0,
        grades: new Map(),
      });
    }
    const subject = subjects.get(key);
    subject.count += 1;

    const grade = toGradeYear(lesson.grade_level);
    const gradeKey = grade === null ? UNPLACED_GRADE : String(grade);
    if (!subject.grades.has(gradeKey)) {
      subject.grades.set(gradeKey, { key: gradeKey, grade, lessons: [] });
    }
    subject.grades.get(gradeKey).lessons.push(lesson);
  }

  return [...subjects.values()]
    .sort((a, b) => {
      if (a.name === null) return 1;
      if (b.name === null) return -1;
      return a.name.localeCompare(b.name);
    })
    .map((subject) => ({
      key: subject.key,
      name: subject.name,
      count: subject.count,
      grades: [...subject.grades.values()]
        .sort((a, b) => {
          if (a.grade === null) return 1;
          if (b.grade === null) return -1;
          return a.grade - b.grade;
        })
        .map((group) => ({
          key: group.key,
          grade: group.grade,
          count: group.lessons.length,
          board: group.lessons.find((lesson) => lesson.base_board)?.base_board || null,
          lessons: [...group.lessons].sort(byLesson),
        })),
    }));
};

export default groupLessonsForBrowse;
